import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, durationLabel, minuteLabel, parseUsd } from '../api/client';
import {
  type Issue,
  type SpecialRate,
  usePublishRates,
  useRates,
  useRateVersions,
  useSaveRates,
} from '../api/hooks';
import {
  Badge,
  Button,
  Card,
  ErrorNote,
  Field,
  Input,
  Issues,
  Loading,
  PageHeader,
  Panel,
  Table,
  Td,
  Th,
} from '../components/ui';
import { useApiError } from '../i18n/useApiError';

interface DraftTier {
  maxMinutes: number;
  baseCents: number;
}

/**
 * Rate editing.
 *
 * Owners enter the final price the driver pays. Saving mints a draft version;
 * publishing is a separate, explicit step, and running sessions keep the
 * version they started on (FR-A-12).
 */
export function RatesPage({ lotId, canEdit }: { lotId: string; canEdit: boolean }) {
  const { t, i18n } = useTranslation();
  const describeError = useApiError();
  const rates = useRates(lotId);
  const versions = useRateVersions(lotId);
  const save = useSaveRates(lotId);
  const publish = usePublishRates(lotId);

  const [tiers, setTiers] = useState<DraftTier[]>([]);
  const [minimumMinutes, setMinimumMinutes] = useState(30);
  const [incrementMinutes, setIncrementMinutes] = useState(30);
  const [dailyCap, setDailyCap] = useState(0);
  const [extraDay, setExtraDay] = useState(0);
  const [specials, setSpecials] = useState<SpecialRate[]>([]);
  const [issues, setIssues] = useState<Issue[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (!rates.data) return;
    setMinimumMinutes(rates.data.version.minimumMinutes);
    setIncrementMinutes(rates.data.version.incrementMinutes);
    setTiers(rates.data.tiers.map((t) => ({ maxMinutes: t.maxMinutes, baseCents: t.baseCents })));
    setDailyCap(rates.data.version.dailyCapCents);
    setExtraDay(rates.data.version.extraDayCents);
    setSpecials(rates.data.specialRates);
  }, [rates.data]);

  if (rates.isPending) return <Loading />;
  if (rates.isError || !rates.data) return <ErrorNote message={t('rates.loadFailed')} />;

  const current = rates.data.version;
  const saveDraft = async () => {
    setError(null);
    try {
      const result = await save.mutateAsync({
        minimumMinutes,
        incrementMinutes,
        dailyCapCents: dailyCap,
        extraDayCents: extraDay,
        tiers,
        specialRates: specials.map(({ id: _id, ...rest }) => rest),
        note: t('rates.editedAt', { time: new Date().toLocaleString(i18n.language) }),
      });
      setIssues(result.issues);
    } catch (e) {
      setError(describeError(e, 'rates.saveFailed'));
    }
  };

  const publishCurrent = async () => {
    setError(null);
    try {
      await publish.mutateAsync(current.id);
      setIssues([]);
    } catch (e) {
      // A refused publish carries the blocking issue list in `details`; feed it
      // to the pre-flight panel instead of dumping raw JSON at the operator.
      if (e instanceof ApiError && Array.isArray(e.details?.issues)) {
        setIssues(e.details.issues as Issue[]);
      }
      setError(describeError(e, 'rates.publishFailed'));
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t('rates.title')}
        hint={t('rates.hint')}
        actions={
          <>
            <Button onClick={() => setShowHistory((v) => !v)}>{t('rates.history')}</Button>
            {canEdit ? (
              <>
                <Button onClick={() => void saveDraft()} disabled={save.isPending}>
                  {t('rates.saveDraft')}
                </Button>
                <Button variant="primary" onClick={() => void publishCurrent()} disabled={publish.isPending}>
                  {t('rates.publish', { version: current.version })}
                </Button>
              </>
            ) : null}
          </>
        }
      />

      {error ? <ErrorNote message={error} /> : null}

      <div className="grid gap-5 lg:grid-cols-[1.05fr_0.95fr]">
        <div className="flex flex-col gap-3">
          <Table>
            <thead>
              <tr>
                <Th>{t('rates.colMax')}</Th>
                <Th align="right">{t('rates.colPrice')}</Th>
              </tr>
            </thead>
            <tbody>
              {tiers.map((tier, index) => (
                <tr key={tier.maxMinutes}>
                  <Td>{t('rates.within', { duration: durationLabel(tier.maxMinutes, t) })}</Td>
                  <Td align="right">
                    <input
                      className="num w-24 rounded border border-hairline px-2 py-1 text-right text-[13px] outline-none focus:border-ink disabled:bg-surface-muted"
                      value={(tier.baseCents / 100).toFixed(2)}
                      disabled={!canEdit}
                      onChange={(event) => {
                        const next = [...tiers];
                        next[index] = { ...tier, baseCents: parseUsd(event.target.value) };
                        setTiers(next);
                      }}
                    />
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>

          <div className="grid gap-3 sm:grid-cols-2">
            <Card>
              <Field label={t('rates.minimumMinutes')}>
                <Input
                  className="num"
                  type="number"
                  min={1}
                  value={minimumMinutes}
                  disabled={!canEdit}
                  onChange={(e) => setMinimumMinutes(Number(e.target.value))}
                />
              </Field>
            </Card>
            <Card>
              <Field label={t('rates.incrementMinutes')}>
                <Input
                  className="num"
                  type="number"
                  min={1}
                  value={incrementMinutes}
                  disabled={!canEdit}
                  onChange={(e) => setIncrementMinutes(Number(e.target.value))}
                />
              </Field>
            </Card>
            <Card>
              <Field label={t('rates.dailyCap')}>
                <Input
                  className="num"
                  value={(dailyCap / 100).toFixed(2)}
                  disabled={!canEdit}
                  onChange={(e) => setDailyCap(parseUsd(e.target.value))}
                />
              </Field>
            </Card>
            <Card>
              <Field label={t('rates.extraDay')}>
                <Input
                  className="num"
                  value={(extraDay / 100).toFixed(2)}
                  disabled={!canEdit}
                  onChange={(e) => setExtraDay(parseUsd(e.target.value))}
                />
              </Field>
            </Card>
          </div>

          {issues ? (
            <Card>
              <h3 className="mb-2 text-[13px] font-bold">{t('rates.preflight')}</h3>
              <Issues issues={issues} />
            </Card>
          ) : null}
        </div>

        <div className="flex flex-col gap-3">
          <h3 className="text-[13px] font-bold">{t('rates.specials')}</h3>
          {specials.map((special, index) => (
            <Card key={special.id} className="flex flex-col gap-2">
              <div className="flex items-baseline justify-between">
                <span className="text-sm font-semibold">{special.name}</span>
                <input
                  className="num w-24 rounded border border-hairline px-2 py-1 text-right text-[13px] outline-none focus:border-ink disabled:bg-surface-muted"
                  value={(special.baseCents / 100).toFixed(2)}
                  disabled={!canEdit}
                  onChange={(event) => {
                    const next = [...specials];
                    next[index] = { ...special, baseCents: parseUsd(event.target.value) };
                    setSpecials(next);
                  }}
                />
              </div>
              <p className="text-xs text-muted">
                {t('rates.specialWindow', {
                  from: minuteLabel(special.entryFromMinute),
                  to: minuteLabel(special.entryToMinute),
                })}
                {special.exitBeforeMinute !== null
                  ? t('rates.specialExit', { time: minuteLabel(special.exitBeforeMinute) })
                  : ''}
              </p>
              <p className="text-[11px] text-faint">{t('rates.specialNote')}</p>
            </Card>
          ))}

          <Panel>
            {t('rates.panel')
              .split('\n')
              .map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
          </Panel>

          {showHistory ? (
            <Card className="flex flex-col gap-2">
              <h3 className="text-[13px] font-bold">{t('rates.history')}</h3>
              {versions.data?.versions.map((version) => (
                <div
                  key={version.id}
                  className="flex items-center justify-between border-t border-hairline pt-2 text-xs"
                >
                  <span className="num">v{version.version}</span>
                  <Badge
                    tone={
                      version.status === 'published'
                        ? 'success'
                        : version.status === 'draft'
                          ? 'warning'
                          : 'neutral'
                    }
                  >
                    {t(
                      version.status === 'published'
                        ? 'rates.published'
                        : version.status === 'draft'
                          ? 'rates.draft'
                          : 'rates.superseded',
                    )}
                  </Badge>
                  <span className="text-faint">
                    {version.publishedAt
                      ? new Date(version.publishedAt).toLocaleString(i18n.language)
                      : t('rates.notPublished')}
                  </span>
                </div>
              ))}
            </Card>
          ) : null}
        </div>
      </div>
    </div>
  );
}
